import { ParamMap } from '@angular/router';
import { pickWalletKey } from '../../../../../models/wallet.model';
import { WalletEventResponse } from '../../../../../models/wallet-response.model';
import { TRANSFER_WALLET_TITLES } from '../../../transfer-history/wallet-transfer-history.constants';
import { WalletTransferStep } from '../../../transfer-history/wallet-transfer-history.model';
import { drawStepsFromEvents } from '../../../transfer-history/wallet-transfer-history.utils';
import {
	DEPOSIT_PAYMENT_RESULT_ARRIVED_CIRCLE,
	DEPOSIT_PENDING_PAYMENT_KEY,
	DEPOSIT_PAYMENT_RESULT_CIRCLE_CLASSES,
	DEPOSIT_PAYMENT_RESULT_STEPS,
	DEPOSIT_PAYMENT_RESULT_TITLE_CLASSES
} from './deposit-payment-result.constants';
import { DepositPaymentResult, DepositPaymentStepView } from './deposit-payment-result.model';

/**
 * The bank's callback, read off the wallet's query string, or null when the page was not
 * reached through one. The amount and target wallet come from what the deposit form left
 * behind as the browser went to the bank, and are taken: a second visit has no payment
 * waiting on it.
 */
export function takeDepositPaymentResult(params: ParamMap): DepositPaymentResult | null {
	const paymentType = params.get('paymentType')?.toLowerCase();
	if (!paymentType) {
		return null;
	}

	let pendingPayment: { amount?: number; target?: string } = {};
	try {
		pendingPayment = JSON.parse(localStorage.getItem(DEPOSIT_PENDING_PAYMENT_KEY) ?? '{}');
		localStorage.removeItem(DEPOSIT_PENDING_PAYMENT_KEY);
	} catch {
		// A malformed local value must not prevent the gateway result from rendering.
	}

	return {
		success: paymentType === 'success',
		canceled: paymentType === 'canceled',
		gateway: params.get('gateway') ?? 'Mellat',
		gatewayId: params.get('gatewayId') ?? '',
		transactionId: params.get('transactionId') ?? '',
		invoiceId: params.get('invoiceId') ?? '',
		amount: typeof pendingPayment.amount === 'number' ? pendingPayment.amount : null,
		targetWallet: pickWalletKey(pendingPayment.target)
	};
}

/**
 * The stops a gateway deposit passes through, as the result sheet lists them: the bank's own
 * page, the bank's confirmation, and the money landing in the trader's wallet.
 *
 * Every stop is drawn as done to begin with — the callback only arrives once the bank has
 * taken the payment — and carries the reference the callback gave it, so the sheet still says
 * something before the events land. The events have the last word on how far it has got.
 */
export function toDepositPaymentSteps(result: DepositPaymentResult): WalletTransferStep[] {
	return [
		{
			title: DEPOSIT_PAYMENT_RESULT_STEPS.gateway,
			dateLabel: null,
			amount: result.amount,
			status: 'done'
		},
		{
			title: DEPOSIT_PAYMENT_RESULT_STEPS.confirm,
			dateLabel: null,
			amount: null,
			status: 'done'
		},
		{
			title: transferStepTitle(result),
			dateLabel: null,
			amount: null,
			status: 'done'
		}
	];
}

/**
 * The stops once the payment's own events are read.
 *
 * No state is passed with them: the callback says the bank took the payment, not that the
 * deposit has landed, so nothing closes the timeline — the number of events decides how many
 * stops are drawn, and the stop after them is the one the money is waiting on. A payment
 * whose events cannot be read keeps the stops as the callback drew them.
 */
export function toDepositPaymentStepsFromEvents(
	steps: readonly WalletTransferStep[],
	events: readonly WalletEventResponse[]
): WalletTransferStep[] {
	return events.length ? drawStepsFromEvents(steps, events, '') : [...steps];
}

/** The stops with the classes their template binds, so the template itself stays plain. */
export function toDepositPaymentStepViews(
	steps: readonly WalletTransferStep[],
	result: DepositPaymentResult
): DepositPaymentStepView[] {
	const arrival = transferStepTitle(result);

	return steps.map((step, index) => ({
		...step,
		// The money's own stop — the wallet it ended up in — is the one the handoff draws
		// green, and only once it is the last stop drawn and behind us.
		circleClass:
			step.status === 'done' && step.title === arrival && index === steps.length - 1
				? DEPOSIT_PAYMENT_RESULT_ARRIVED_CIRCLE
				: DEPOSIT_PAYMENT_RESULT_CIRCLE_CLASSES[step.status],
		titleClass: DEPOSIT_PAYMENT_RESULT_TITLE_CLASSES[step.status]
	}));
}

/** The last stop of a gateway deposit: the money moved on into the wallet it was aimed at. */
function transferStepTitle(result: DepositPaymentResult): string {
	return `انتقال از درگاه بانکی به ${TRANSFER_WALLET_TITLES[result.targetWallet]}`;
}
